import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Form, Modal, Button, Alert, Card, Badge, ListGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faUsers, faFilter, faEye, faPaperPlane, faSpinner, faPaperclip, faTrash, faFileAlt, faEdit, faTimes } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// My components
import HeaderCard from "../../components/common/HeaderCard";
import BackButton from "../../components/common/BackButton";
import Loader from "../../components/common/Loader";
import MultiSelectDropdown from "../../components/common/MultiSelectDropdown";

// My css
import css from "../../css/admin/home-page.module.css";

import { SERVER_ORIGIN } from "../../utilities/constants";

const MassEmailPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [filterOptions, setFilterOptions] = useState({
        regions: [],
        cities: [],
        institutes: [],
        schools: [],
        userRoles: []
    });

    // Form state
    const [emailForm, setEmailForm] = useState({
        subject: "",
        message: ""
    });

    // Attachment state
    const [attachments, setAttachments] = useState([]);

    // Email templates state
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showTemplateModal, setShowTemplateModal] = useState(false);

    // Filter state
    const [filters, setFilters] = useState({
        userRole: [],
        region: [],
        city: [],
        institute: [],
        school: []
    });

    // Recipients state
    const [recipients, setRecipients] = useState([]);
    const [recipientCount, setRecipientCount] = useState(0);
    const [showPreview, setShowPreview] = useState(false);

    const navigate = useNavigate();
    const userType = sessionStorage.getItem('userType');

    useEffect(() => {
        // if (isReadOnly) {
        //     navigate("/admin/services");
        //     return;
        // }
        fetchFilterOptions();
        fetchEmailTemplates();
    }, []);

    const fetchEmailTemplates = async () => {
        try {
            const headers = {
                "Content-Type": "application/json",
                "auth-token": sessionStorage.getItem("token"),
            };

            const adminId = "yuva@gmail.com";
            const adminPassword = "admin@yuva123";
            const basicAuth = btoa(`${adminId}:${adminPassword}`);
            headers.Authorization = `Basic ${basicAuth}`;

            const response = await fetch(`${SERVER_ORIGIN}/api/bulk-email/templates`, {
                method: "GET",
                headers: headers
            });

            const result = await response.json();

            if (response.ok) {
                setEmailTemplates(result.data || []);
            } else {
                console.error("Failed to fetch email templates:", result);
            }
        } catch (error) {
            console.error("Error fetching email templates:", error);
        }
    };

    const handleTemplateSelect = async (template) => {
        try {
            const headers = {
                "Content-Type": "application/json",
                "auth-token": sessionStorage.getItem("token"),
            };

            const adminId = "yuva@gmail.com";
            const adminPassword = "admin@yuva123";
            const basicAuth = btoa(`${adminId}:${adminPassword}`);
            headers.Authorization = `Basic ${basicAuth}`;

            // The templates list endpoint does NOT include `message`.
            // Fetch the full template by slug to get the message body.
            const response = await fetch(`${SERVER_ORIGIN}/api/bulk-email/templates/${template.slug}`, {
                method: "GET",
                headers
            });

            const result = await response.json();

            if (!response.ok) {
                toast.error(result.statusText || "Failed to load template");
                return;
            }

            const fullTemplate = result.data;
            setSelectedTemplate(fullTemplate);
            setEmailForm({
                subject: fullTemplate.subject || "",
                message: fullTemplate.message || ""
            });
            setShowTemplateModal(false);
            toast.success(`Template "${fullTemplate.name}" loaded. You can now edit the content.`);
        } catch (error) {
            console.error("Error loading template:", error);
            toast.error("Failed to load template");
        }
    };

    const clearTemplate = () => {
        setSelectedTemplate(null);
        setEmailForm({
            subject: "",
            message: ""
        });
    };

    // Group templates by category
    const groupedTemplates = emailTemplates.reduce((acc, template) => {
        const category = template.category || 'general';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(template);
        return acc;
    }, {});

    const getCategoryLabel = (category) => {
        const labels = {
            'renewal': 'MOU Renewal',
            'onboarding': 'Onboarding',
            'terms_conditions': 'Terms & Conditions',
            'student_upload': 'Student Upload',
            'general': 'General',
            'reminder': 'Reminders'
        };
        return labels[category] || category;
    };

    const getCategoryColor = (category) => {
        const colors = {
            'renewal': 'warning',
            'onboarding': 'success',
            'terms_conditions': 'danger',
            'student_upload': 'info',
            'general': 'secondary',
            'reminder': 'primary'
        };
        return colors[category] || 'secondary';
    };

    const fetchFilterOptions = async () => {
        setIsLoading(true);
        try {
            const headers = {
                "Content-Type": "application/json",
                "auth-token": sessionStorage.getItem("token"),
            };

            // Add basic auth for bulk-email API
            const adminId = "yuva@gmail.com";
            const adminPassword = "admin@yuva123";
            const basicAuth = btoa(`${adminId}:${adminPassword}`);
            headers.Authorization = `Basic ${basicAuth}`;

            console.log("Fetching filter options...", {
                url: `${SERVER_ORIGIN}/api/bulk-email/filter-options`,
                headers: headers
            });

            const response = await fetch(`${SERVER_ORIGIN}/api/bulk-email/filter-options`, {
                method: "GET",
                headers: headers
            });

            console.log("Filter options response:", response.status, response.statusText);

            const result = await response.json();
            console.log("Filter options result:", result);

            if (response.ok) {
                setFilterOptions(result.data);
                console.log("Filter options set:", result.data);
            } else {
                console.error("Failed to fetch filter options:", result);
                toast.error(result.statusText || "Failed to fetch filter options");
            }
        } catch (error) {
            console.error("Error fetching filter options:", error);
            toast.error("Failed to fetch filter options");
        }
        setIsLoading(false);
    };

    const previewRecipients = async () => {
        if (!filters.userRole.length && !filters.region.length && !filters.city.length && !filters.institute.length && !(filters.school && filters.school.length)) {
            toast.error("Please select at least one filter");
            return;
        }

        setIsLoading(true);
        try {
            const headers = {
                "Content-Type": "application/json",
                "auth-token": sessionStorage.getItem("token"),
            };

            // Add basic auth for bulk-email API
            const adminId = "yuva@gmail.com";
            const adminPassword = "admin@yuva123";
            const basicAuth = btoa(`${adminId}:${adminPassword}`);
            headers.Authorization = `Basic ${basicAuth}`;

            const response = await fetch(`${SERVER_ORIGIN}/api/bulk-email/preview-recipients`, {
                method: "POST",
                headers: headers,
                body: JSON.stringify({ filters })
            });

            const result = await response.json();

            if (response.ok) {
                setRecipients(result.data.recipients);
                setRecipientCount(result.data.count);
                if (result.data.count === 0) {
                    toast.error("No recipients found with current filters");
                } else {
                    toast.success(`Found ${result.data.count} recipients`);
                }
            } else {
                toast.error(result.statusText || "Failed to preview recipients");
            }
        } catch (error) {
            console.error("Error previewing recipients:", error);
            toast.error("Failed to preview recipients");
        }
        setIsLoading(false);
    };

    const sendBulkEmail = async () => {
        if (!emailForm.subject.trim() || !emailForm.message.trim()) {
            toast.error("Subject and message are required");
            return;
        }

        if (recipientCount === 0) {
            toast.error("Please preview recipients first");
            return;
        }

        setIsSending(true);
        try {
            // Prepare form data for multipart/form-data (to support file uploads)
            const formData = new FormData();
            formData.append("subject", emailForm.subject);
            formData.append("message", emailForm.message);
            formData.append("filters", JSON.stringify(filters));

            // Append attachments
            attachments.forEach((file, index) => {
                formData.append("attachments", file);
            });

            // Add basic auth for bulk-email API
            const adminId = "yuva@gmail.com";
            const adminPassword = "admin@yuva123";
            const basicAuth = btoa(`${adminId}:${adminPassword}`);

            const response = await fetch(`${SERVER_ORIGIN}/api/bulk-email/send-chapter`, {
                method: "POST",
                headers: {
                    "auth-token": sessionStorage.getItem("token"),
                    "Authorization": `Basic ${basicAuth}`
                    // Don't set Content-Type - browser will set it with boundary for FormData
                },
                body: formData
            });

            const result = await response.json();

            if (response.ok) {
                toast.success(`Email sent successfully to ${result.data.recipientCount} recipients!`);

                // Reset form
                setEmailForm({ subject: "", message: "" });
                setFilters({ userRole: [], region: [], city: [], institute: [], school: [] });
                setRecipients([]);
                setRecipientCount(0);
                setAttachments([]);
            } else {
                toast.error(result.statusText || "Failed to send email");
            }
        } catch (error) {
            console.error("Error sending email:", error);
            toast.error("Failed to send email");
        }
        setIsSending(false);
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));

        // Reset recipients when filters change
        setRecipients([]);
        setRecipientCount(0);
    };


    const getDashboardPath = () => {
        if (userType === 'admin' || userType === 'regionem') {
            return '/admin/services';
        } else if (userType === 'em') {
            return '/em/services';
        }
        return '/admin/services';
    };
    if (isLoading && !filterOptions.regions.length) {
        return <Loader />;
    }

    return (
        <div className={css.outerDiv}>
            <HeaderCard>
                <BackButton text="Back to Dashboard" customPath="/em/services" />
                <h1 className="headerTitle">
                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "10px" }} />
                    Mass Email Communication
                </h1>
                <hr />
                <p className="headerSubtitle">
                    Send targeted emails to users in your region and city. As a Chapter EM, you can email students and institutes within your geographical area.
                </p>
                {/* <Alert variant="info">
                    <strong>Note:</strong> Your emails are restricted to users in your region and city for targeted communication.
                </Alert> */}
            </HeaderCard>

            <div className="container-fluid mt-4">
                <Row>
                    {/* Filters Section */}
                    <Col lg={4} md={12} className="mb-4">
                        <Card>
                            <Card.Header>
                                <h5><FontAwesomeIcon icon={faFilter} className="me-2" />Email Filters</h5>
                            </Card.Header>
                            <Card.Body>
                                {/* User Role Filter */}
                                <MultiSelectDropdown
                                    label="User Roles"
                                    options={[
                                        { value: 'student', label: 'Students' },
                                        { value: 'institute', label: 'Institutes' },
                                        { value: 'school', label: 'Schools (Thalir)' }
                                    ]}
                                    selectedValues={filters.userRole}
                                    onChange={(values) => handleFilterChange('userRole', values)}
                                    placeholder="Select user roles..."
                                    helpText="Choose which types of users (students, institutes or Thalir schools) to include in the email"
                                />

                                {/* Institute Filter */}
                                <MultiSelectDropdown
                                    label="Institutes (Optional)"
                                    options={filterOptions.institutes}
                                    selectedValues={filters.institute}
                                    onChange={(values) => handleFilterChange('institute', values)}
                                    placeholder="Select institutes..."
                                    helpText="Leave empty to include all institutes in your area"
                                />
                                <MultiSelectDropdown
                                    label="Schools (Optional)"
                                    options={filterOptions.schools}
                                    selectedValues={filters.school}
                                    onChange={(values) => handleFilterChange('school', values)}
                                    placeholder="Select schools..."
                                    helpText="Leave empty to include all schools in your area"
                                />

                                <div className="d-grid">
                                    <Button
                                        variant="outline-primary"
                                        onClick={previewRecipients}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                        ) : (
                                            <FontAwesomeIcon icon={faEye} className="me-2" />
                                        )}
                                        Preview Recipients
                                    </Button>
                                </div>

                                {recipientCount > 0 && (
                                    <Alert variant="success" className="mt-3">
                                        <FontAwesomeIcon icon={faUsers} className="me-2" />
                                        <strong>{recipientCount}</strong> recipients found
                                        <Button
                                            variant="link"
                                            size="sm"
                                            onClick={() => setShowPreview(true)}
                                            className="ms-2 p-0"
                                        >
                                            View List
                                        </Button>
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Email Composition Section */}
                    <Col lg={8} md={12}>
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0"><FontAwesomeIcon icon={faEnvelope} className="me-2" />Compose Email</h5>
                                <div>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => setShowTemplateModal(true)}
                                        className="me-2"
                                    >
                                        <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                                        Use Template
                                    </Button>
                                    {selectedTemplate && (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={clearTemplate}
                                        >
                                            <FontAwesomeIcon icon={faTimes} className="me-1" />
                                            Clear
                                        </Button>
                                    )}
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {selectedTemplate && (
                                    <Alert variant="info" className="mb-3">
                                        <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                        Using template: <strong>{selectedTemplate.name}</strong>
                                        <Badge bg={getCategoryColor(selectedTemplate.category)} className="ms-2">
                                            {getCategoryLabel(selectedTemplate.category)}
                                        </Badge>
                                        <div className="mt-1 small text-muted">
                                            <FontAwesomeIcon icon={faEdit} className="me-1" />
                                            You can edit the content below before sending
                                        </div>
                                    </Alert>
                                )}
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Subject *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter email subject"
                                            value={emailForm.subject}
                                            onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Message *</Form.Label>
                                        <ReactQuill
                                            theme="snow"
                                            value={emailForm.message}
                                            onChange={(value) => setEmailForm(prev => ({ ...prev, message: value }))}
                                            placeholder="Enter your message here..."
                                            style={{ height: "250px", marginBottom: "50px" }}
                                            modules={{
                                                toolbar: [
                                                    [{ 'header': [1, 2, 3, false] }],
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                    [{ 'align': [] }],
                                                    ['link'],
                                                    [{ 'color': [] }, { 'background': [] }],
                                                    ['clean']
                                                ]
                                            }}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Attachments (PDF, DOC, DOCX, XLS, XLSX)</Form.Label>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <input
                                                type="file"
                                                id="attachment-input-chapter"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx"
                                                multiple
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    const files = Array.from(e.target.files || []);
                                                    if (files.length === 0) return;

                                                    const validFiles = files.filter(file => {
                                                        const ext = file.name.split('.').pop().toLowerCase();
                                                        const validExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
                                                        if (!validExtensions.includes(ext)) {
                                                            toast.error(`${file.name} is not a valid file type. Only PDF, DOC, DOCX, XLS, XLSX are allowed.`);
                                                            return false;
                                                        }
                                                        if (file.size > 10 * 1024 * 1024) { // 10MB limit
                                                            toast.error(`${file.name} is too large. Maximum size is 10MB.`);
                                                            return false;
                                                        }
                                                        return true;
                                                    });

                                                    if (validFiles.length > 0) {
                                                        setAttachments(prev => [...prev, ...validFiles]);
                                                        toast.success(`${validFiles.length} file(s) added`);
                                                    }

                                                    // Reset input to allow selecting the same file again
                                                    e.target.value = '';
                                                }}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => document.getElementById('attachment-input-chapter').click()}
                                            >
                                                <FontAwesomeIcon icon={faPaperclip} className="me-2" />
                                                Browse Files
                                            </Button>
                                            <span className="text-muted small">
                                                {attachments.length > 0
                                                    ? `${attachments.length} file(s) selected`
                                                    : 'No files selected'}
                                            </span>
                                        </div>
                                        <Form.Text className="text-muted d-block mb-2">
                                            Maximum file size: 10MB per file. You can select multiple files.
                                        </Form.Text>
                                        {attachments.length > 0 && (
                                            <ListGroup className="mt-2">
                                                {attachments.map((file, index) => (
                                                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                                                        <span>
                                                            <FontAwesomeIcon icon={faPaperclip} className="me-2 text-primary" />
                                                            {file.name} <span className="text-muted">({(file.size / 1024).toFixed(2)} KB)</span>
                                                        </span>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => {
                                                                setAttachments(prev => prev.filter((_, i) => i !== index));
                                                                toast.success('File removed');
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </Button>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        )}
                                    </Form.Group>

                                    <div className="d-grid mt-3">
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            onClick={sendBulkEmail}
                                            disabled={isSending || recipientCount === 0 || !emailForm.subject.trim() || !emailForm.message.trim()}
                                        >
                                            {isSending ? (
                                                <>
                                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                                    Sending Email...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                                                    Send to {recipientCount} Recipients
                                                    {attachments.length > 0 && ` (with ${attachments.length} attachment${attachments.length > 1 ? 's' : ''})`}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Recipients Preview Modal */}
            <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faUsers} className="me-2" />
                        Email Recipients ({recipientCount})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {recipients.map((recipient, index) => (
                        <div key={index} className="d-flex justify-content-between align-items-center border-bottom py-2">
                            <div>
                                <strong>{recipient.name || 'Unknown'}</strong><br />
                                <small className="text-muted">{recipient.email}</small>
                            </div>
                            <div className="text-end">
                                <Badge bg="secondary" className="me-1">{recipient.userType}</Badge>
                                {recipient.region && <Badge bg="info" className="me-1">{recipient.region}</Badge>}
                                {recipient.city && <Badge bg="warning">{recipient.city}</Badge>}
                            </div>
                        </div>
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPreview(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Email Templates Modal */}
            <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                        Email Templates Library
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    {emailTemplates.length === 0 ? (
                        <Alert variant="info">
                            <p className="mb-0">No email templates available. Templates will be automatically created when the system initializes.</p>
                        </Alert>
                    ) : (
                        Object.entries(groupedTemplates).map(([category, templates]) => (
                            <div key={category} className="mb-4">
                                <h6 className="text-uppercase text-muted mb-3">
                                    <Badge bg={getCategoryColor(category)} className="me-2">
                                        {getCategoryLabel(category)}
                                    </Badge>
                                    <small>({templates.length} templates)</small>
                                </h6>
                                <ListGroup>
                                    {templates.map((template) => (
                                        <ListGroup.Item
                                            key={template.slug}
                                            action
                                            onClick={() => handleTemplateSelect(template)}
                                            className="d-flex justify-content-between align-items-start"
                                        >
                                            <div className="ms-2 me-auto">
                                                <div className="fw-bold">{template.name}</div>
                                                <small className="text-muted">{template.description}</small>
                                                <div className="mt-1">
                                                    <small className="text-secondary">
                                                        <strong>Subject:</strong> {template.subject.length > 60
                                                            ? template.subject.substring(0, 60) + '...'
                                                            : template.subject}
                                                    </small>
                                                </div>
                                                {template.variables && template.variables.length > 0 && (
                                                    <div className="mt-1">
                                                        <small className="text-info">
                                                            <strong>Variables:</strong> {template.variables.map(v => `{{${v.name}}}`).join(', ')}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                            <Button variant="outline-primary" size="sm">
                                                Use
                                            </Button>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </div>
                        ))
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MassEmailPage; 